<template>
  <!-- Utopie shell: Pre-kínder · Matemáticas → OAs → cantidades → stream → PDF. -->
  <div class="aig">
    <div v-if="step !== 'done' && step !== 'streaming'" class="pi-mat">
      <header class="pi-mat-head">
        <div class="course-context" v-if="selectedGrade && selectedSubject">
          <span>{{ selectedGradeLabel }}</span>
          <i aria-hidden="true">·</i>
          <span>{{ selectedSubject.name }}</span>
        </div>
        <strong>{{ configHeadTitle }}</strong>
        <span>{{ configHeadSub }}</span>
        <p class="pi-mat-hint" v-html="configHeadHint"></p>
      </header>

      <div class="pi-mat-stage">
        <div ref="configScroll" class="pi-mat-scroll">
          <p v-if="bootError" class="pi-mat-empty">{{ bootError }}</p>

          <div v-else-if="step === 'pick'" class="pi-mat-pick">
            <label class="pi-mat-field">
              <span>Curso</span>
              <select
                class="pi-mat-select"
                :value="gradeSelectValue"
                :disabled="loadingGrades"
                @change="onGradeSelect($event)"
              >
                <!-- value string; Prek=0 no puede ir como number o el <select> se rompe -->
                <option value="">
                  {{ loadingGrades ? "Cargando cursos…" : "Selecciona un curso" }}
                </option>
                <option
                  v-for="g in grades"
                  :key="'g-' + g.code"
                  :value="String(g.code)"
                >
                  {{ g.original_name }}
                </option>
              </select>
            </label>
            <label class="pi-mat-field">
              <span>Asignatura</span>
              <select
                ref="subjectSelect"
                class="pi-mat-select"
                :value="subjectSelectValue"
                :disabled="!selectedGrade || loadingSubjects"
                @change="onSubjectSelect($event)"
              >
                <!-- No disabled: si value='' y el placeholder está disabled, el
                     browser muestra la 1ª asignatura (p. ej. Matemáticas) sin
                     disparar @change → Siguiente queda apagado. -->
                <option value="">
                  {{
                    !selectedGrade
                      ? "Primero elige un curso"
                      : loadingSubjects
                      ? "Cargando asignaturas…"
                      : "Selecciona una asignatura"
                  }}
                </option>
                <option
                  v-for="s in subjects"
                  :key="'s-' + s.code"
                  :value="String(s.code)"
                >
                  {{ s.name }}
                </option>
              </select>
            </label>
          </div>

          <div v-else-if="step === 'oas'" class="pi-mat-oa-list">
            <p v-if="loadingOas" class="pi-mat-loading">
              Cargando objetivos de {{ selectedGradeLabel }}
              <template v-if="selectedSubject"> · {{ selectedSubject.name }}</template>…
            </p>
            <p v-else-if="!oas.length" class="pi-mat-empty">
              No hay objetivos de aprendizaje para
              {{ selectedGradeLabel }}
              <template v-if="selectedSubject"> · {{ selectedSubject.name }}</template>.
            </p>
            <template v-else>
              <button
                v-for="(oa, idx) in oas"
                :key="oa.id"
                type="button"
                class="pi-mat-oa"
                :class="{
                  'is-selected': oa.selected,
                  'is-pulse-guide':
                    !objectivePulseStopped && !selectedOas.length && idx === 0,
                }"
                @click="toggleOa(oa)"
              >
                <span class="pi-mat-oa-check">{{ oa.selected ? "✓" : "" }}</span>
                <span class="pi-mat-oa-copy">
                  <strong>{{ oa.name }}</strong>
                  <span>{{ oaShortSummary(oa) }}</span>
                </span>
              </button>
            </template>
          </div>

          <div
            v-else-if="step === 'indicators' && currentCountOa"
            class="pi-mat-counts"
          >
            <p class="pi-mat-counts-lead">
              OA {{ currentOaIndex + 1 }} de {{ selectedOas.length }} ·
              {{ currentCountOa.name }}
            </p>
            <template v-if="usesPeldanosUi">
              <p class="pi-mat-max-hint">
                Cuántos de cada peldaño (por defecto 1·1·1·1). Máximo
                {{ peldanosMaxPorOa }} por OA.
              </p>
              <div
                v-for="p in [1, 2, 3, 4]"
                :key="'peldano-' + p"
                class="pi-mat-count-row"
                :class="{ 'is-disabled-peldano': peldanoBloqueado(currentCountOa, p) }"
              >
                <span class="pi-mat-peldano-label">
                  <b>{{ p }} {{ peldanoLabel(p) }}</b>
                  <small class="pi-mat-peldano-sub">{{ peldanoSub(p) }}</small>
                  <small
                    v-if="peldanoBloqueado(currentCountOa, p)"
                    class="pi-mat-peldano-motivo"
                  >
                    · {{ peldanoMotivo(currentCountOa, p) }}
                  </small>
                </span>
                <div class="pi-count-stepper">
                  <button
                    type="button"
                    :disabled="peldanoBloqueado(currentCountOa, p)"
                    @click="bumpPeldano(currentCountOa, p, -1)"
                  >
                    −
                  </button>
                  <b>{{ peldanoCount(currentCountOa, p) }}</b>
                  <button
                    type="button"
                    :disabled="peldanoBloqueado(currentCountOa, p)"
                    @click="bumpPeldano(currentCountOa, p, 1)"
                  >
                    +
                  </button>
                </div>
              </div>
            </template>
            <template v-else>
              <p class="pi-mat-max-hint">Máximo 5 actividades por OA (mínimo 1).</p>
              <template v-if="currentCountOa.indicators.length">
                <div
                  v-for="ind in currentCountOa.indicators"
                  :key="ind.id"
                  class="pi-mat-count-row"
                >
                  <span v-html="ind.label"></span>
                  <div class="pi-count-stepper">
                    <button type="button" @click="bump(ind, -1)">−</button>
                    <b>{{ ind.count }}</b>
                    <button type="button" @click="bump(ind, 1)">+</button>
                  </div>
                </div>
              </template>
              <div v-else class="pi-mat-count-row">
                <span>Actividades para este OA</span>
                <div class="pi-count-stepper">
                  <button type="button" @click="bump(currentCountOa, -1)">−</button>
                  <b>{{ currentCountOa.count }}</b>
                  <button type="button" @click="bump(currentCountOa, 1)">+</button>
                </div>
              </div>
            </template>
          </div>
        </div>

        <footer
          class="pi-mat-footer"
          :class="{
            'pi-mat-footer--split': step === 'indicators' || step === 'oas',
          }"
        >
          <button
            v-if="step === 'pick'"
            type="button"
            class="pi-direct-btn"
            :class="{ 'pi-pulse-cta': canAdvancePick }"
            :disabled="!canAdvancePick || loadingOas"
            @click="goToOas"
          >
            {{ loadingOas ? "Cargando OAs…" : "OK · Siguiente" }}
          </button>
          <template v-else-if="step === 'oas'">
            <button
              type="button"
              class="pi-direct-btn secondary"
              @click="step = 'pick'"
            >
              Volver
            </button>
            <button
              type="button"
              class="pi-direct-btn"
              :class="{ 'pi-pulse-cta': selectedOas.length }"
              :disabled="!selectedOas.length || loadingOas"
              @click="goToIndicators"
            >
              OK · Siguiente
            </button>
          </template>
          <template v-else-if="step === 'indicators'">
            <button
              type="button"
              class="pi-direct-btn secondary"
              @click="backFromCounts"
            >
              Volver
            </button>
            <button
              type="button"
              class="pi-direct-btn"
              :class="{
                'pi-pulse-cta': canAdvanceCounts && !generating,
                'is-loading': generating,
              }"
              :disabled="!canAdvanceCounts || generating"
              @click="advanceCounts"
            >
              {{ generating ? "Generando…" : countsActionLabel }}
            </button>
          </template>
        </footer>
      </div>
    </div>

    <div v-else-if="step === 'streaming' || step === 'done'" class="aig-stream">
      <MaterialStreamCards
        ref="streamCards"
        :cards="streamCards"
        :progress="streamProgress"
        :progress-label="streamProgressLabel"
        :title="streamTitle"
        :show-loader="showStreamLoader"
      />
      <p v-if="streamError" class="aig-error">{{ streamError }}</p>
      <ul
        v-if="avisosPedagogicos.length"
        class="aig-avisos-pedagogicos"
        aria-live="polite"
      >
        <li v-for="(a, i) in avisosPedagogicos" :key="'av-' + i">
          <template v-if="a.oa && a.oa !== '*'">{{ a.oa }}: </template>
          {{ a.motivo }}
          <span v-if="a.accion"> — {{ a.accion }}</span>
        </li>
      </ul>

      <!-- Footer fijo: descarga. El feedback de producto vive en FeedbackComponent. -->
      <footer class="aig-stream-footer">
        <a
          v-if="pdfUrl"
          :href="pdfUrl"
          target="_blank"
          rel="noopener"
          class="pi-direct-btn pi-pulse-cta"
        >
          Descargar material
        </a>
        <button
          v-else
          type="button"
          class="pi-direct-btn"
          :class="{
            'is-loading pi-pulse-cta': showDownloadCta,
            'is-waiting': !showDownloadCta,
          }"
          disabled
        >
          {{ showDownloadCta ? "Armando PDF…" : "Descargar material" }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import informationService from "@/services/informationService";
import customTestService from "@/services/customTestService";
import customMaterialService from "@/services/customMaterialService";
import assistedMaterialService from "@/services/assistedMaterialService";
import MaterialStreamCards from "./MaterialStreamCards.vue";
import {
  familyForOa,
  familyForTipo,
  isVisualPilot,
  resolvePrekVisualOaNumber,
  wait,
} from "@/utils/prekVisualStream";
import { buildSceneFromConfig } from "@/utils/prekStreamScenes";
import SIN_PELDANO_POR_GRADE from "@/data/sinPeldano";
import { scrollToElementTop } from "@/utils/scrollToTop";

// El ÚNICO punto por donde este componente habla con el backend.
//
// Existe para que el demo público monte este mismo componente —no una copia— y le
// inyecte un origen de fixture. La versión anterior del demo heredaba de acá y
// sobrescribía cinco métodos internos; cuando el componente se reescribió, esos
// métodos dejaron de existir, los overrides quedaron sin interceptar nada y el
// demo pasó a postear contra la API de verdad sin que nada avisara. La superficie
// que hay que mantener estable pasa a ser esta lista de ocho operaciones.
//
// Si agregas una llamada al backend, va acá: llamar a un servicio directo desde
// un método vuelve a abrir el agujero.
export const BACKEND_REAL = {
  getGrades: () => informationService.getPieGrades(),
  getSubjects: (gradeOriginalName) =>
    customTestService.getSubjectHashtags(gradeOriginalName),
  getOas: (subjectCode) => customTestService.getTagsFiltered(subjectCode),
  generateReadingText: (payload) =>
    assistedMaterialService.generateAndSaveReadingText(payload),
  generateVisualMathQuestions: (payload) =>
    assistedMaterialService.generateVisualMathQuestions(payload),
  generatePageQuestions: (payload) =>
    assistedMaterialService.generatePageQuestions(payload),
  planificarMaterialProgresion: (payload) =>
    assistedMaterialService.planificarMaterialProgresion(payload),
  createMaterial: (payload, config) =>
    customMaterialService.createChatbotMaterial(payload, config),
};

export const OPERACIONES_BACKEND = Object.keys(BACKEND_REAL);

const SPECIAL_SUBJECTS = [
  "Psicología",
  "Fonoaudiología",
  "Psicopedagogía",
  "Emocional sensorial y motriz",
  "Habilidades adaptativas",
];

// Debe coincidir con backend settings UTOPIE7_GRADES (Prek=0, Kínder=1, B1–B8=2–9).
const MOTOR_GRADES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

// Escalera de andamio (dificultad.py). Default producto: 1·1·1·1.
const DEFAULT_PELDANOS = { 1: 1, 2: 1, 3: 1, 4: 1 };
// Literales Claude (cierre/RESPUESTAS.md): subtítulo en gris debajo.
const PELDANO_LABELS = {
  1: "Con dibujo",
  2: "Con dibujo y cálculo",
  3: "Con un dato menos",
  4: "Resolución de problemas",
};
const PELDANO_SUBS = {
  1: "la respuesta se ve en la imagen",
  2: "el dibujo da los datos, hay que operar",
  3: "se da el resultado y falta un dato, o se pide la regla",
  4: "sólo el enunciado, sin dibujo",
};
const PELDANOS_MAX_POR_OA = 12;
// El flag UTOPIE7_PLANIFICADOR vive solo en el backend (hoy False).
// El FE igual llama a planificar: expandir_pedido + avisos; sin contexto GPT.

export default {
  name: "MaterialAiGenerate",
  components: { MaterialStreamCards },
  props: {
    context: { type: Object, required: false, default: () => ({}) },
    canWork: { type: Boolean, required: false, default: false },
    embeddedInVitrina: { type: Boolean, required: false, default: false },
    // Origen de datos. Por defecto el backend real; el demo público inyecta acá
    // un origen de fixture y no toca nada más del componente.
    dataSource: { type: Object, required: false, default: null },
  },
  data() {
    return {
      step: "pick",
      booting: false,
      bootError: "",
      grades: [],
      loadingGrades: false,
      selectedGrade: null,
      subjects: [],
      loadingSubjects: false,
      selectedSubject: null,
      oas: [],
      loadingOas: false,
      generating: false,
      createdName: "",
      pdfUrl: null,
      streamCards: [],
      streamProgress: 0,
      streamProgressLabel: "Preparando…",
      streamTitle: "Generando material",
      streamError: null,
      showStreamLoader: false,
      showDownloadCta: false,
      generationToken: 0,
      currentOaIndex: 0,
      objectivePulseStopped: false,
      countsVersion: 0,
      generationAvisos: [],
      codigoHoja: null,
      // Cómo parar el guión del demo. `null` en el producto real.
      detenerAutoplay: null,
    };
  },
  computed: {
    /** Las ocho operaciones de backend, reales o inyectadas.
     *
     * Dos formas de inyectar, y las dos hacen falta: la prop `dataSource` cuando
     * alguien monta este componente directamente, y `context.demo` cuando lo monta
     * el chat — `ChatMessageComponent` le pasa props fijas a un `custom-component`
     * y `dataSource` no está entre ellas, así que el modo demo viaja dentro del
     * contexto, igual que en PlanificationDemo.
     */
    api() {
      const inyectado = this.origenInyectado();
      if (!inyectado) return BACKEND_REAL;
      // Se completa con las reales en vez de exigir las ocho: un origen parcial
      // (p. ej. un demo que solo replica la generación) sigue siendo válido, y lo
      // que no declare va al backend de verdad —que es visible en la red, no un
      // fallo silencioso.
      return { ...BACKEND_REAL, ...inyectado };
    },
    selectedOas() {
      return this.oas.filter((oa) => oa.selected);
    },
    totalQuestions() {
      return this.selectedOas.reduce((sum, oa) => sum + this.oaTotal(oa), 0);
    },
    currentCountOa() {
      return this.selectedOas[this.currentOaIndex] || null;
    },
    usesPeldanosUi() {
      return this.usesVisualStream();
    },
    peldanosMaxPorOa() {
      return PELDANOS_MAX_POR_OA;
    },
    avisosPedagogicos() {
      return (this.generationAvisos || []).filter((a) => {
        if (!a) return false;
        const motivo = String(a.motivo || "");
        const codigo = String(a.codigo || "");
        // Interno: peldaño ausente en escalera — el usuario no lo necesita.
        if (/peldano_\d+_omitido/.test(codigo) || /no tiene peldaño/i.test(motivo)) {
          return false;
        }
        if (codigo === "hoja_corta" || codigo === "oa_en_ajuste") return true;
        if (a.oa === "*") return /pidieron|pediste|salieron|salió/i.test(motivo);
        if (!a.oa || a.oa === "*") return false;
        return /ejercicios distintos|todavía está en ajuste/i.test(motivo);
      });
    },
    canAdvanceCounts() {
      // countsVersion forces recompute after bump (Vue 2 nested reactivity).
      void this.countsVersion;
      const oa = this.currentCountOa;
      if (!oa) return false;
      if (this.usesPeldanosUi) {
        return this.oaTotal(oa) > 0;
      }
      if (oa.indicators && oa.indicators.length) {
        return oa.indicators.some((i) => (Number(i.count) || 0) > 0);
      }
      return (Number(oa.count) || 0) > 0;
    },
    countsActionLabel() {
      const last = this.currentOaIndex >= this.selectedOas.length - 1;
      return last ? "OK · Generar material" : "OK · Siguiente OA";
    },
    configHeadTitle() {
      if (this.step === "pick") return "Curso y asignatura";
      if (this.step === "indicators") return "Cantidad de actividades";
      return "Objetivos de aprendizaje";
    },
    configHeadSub() {
      if (this.step === "pick") {
        return "Elige el curso y la asignatura del material.";
      }
      if (this.step === "indicators") {
        return "Indica cuántas actividades quieres por indicador.";
      }
      return "Selecciona los OAs que quieres transformar en ejercicios.";
    },
    configHeadHint() {
      if (this.step === "pick") {
        return "Después eliges los OAs y cuántas actividades generar.";
      }
      if (this.step === "indicators" && this.currentCountOa) {
        return (
          "Indicadores opcionales para <b>" +
          this.escapeHtml(this.currentCountOa.name) +
          "</b> (" +
          (this.currentOaIndex + 1) +
          "/" +
          this.selectedOas.length +
          ")."
        );
      }
      return "Luego podrás definir cuántas actividades crear para cada uno.";
    },
    gradeSelectValue() {
      // String siempre: code 0 (Pre-kinder) es falsy como number en el DOM.
      return this.selectedGrade == null ? "" : String(this.selectedGrade.code);
    },
    subjectSelectValue() {
      return this.selectedSubject == null
        ? ""
        : String(this.selectedSubject.code);
    },
    canAdvancePick() {
      return !!(this.selectedGrade && this.selectedSubject);
    },
    selectedGradeLabel() {
      return (
        (this.selectedGrade && this.selectedGrade.original_name) || "Curso"
      );
    },
  },
  watch: {
    step() {
      this.$nextTick(() => {
        this.scrollConfigTop();
        // Una sola vez por paso, y también al entrar al stream: ese bloque mide
        // casi una pantalla entera y tiene su propio scroll adentro, así que en
        // un teléfono —sin esto— el visitante se queda mirando el borde de
        // arriba mientras las tarjetas se dibujan más abajo. Lo que sigue
        // prohibido es seguir el scroll de la página tarjeta a tarjeta: de eso
        // se encarga el feed, por dentro.
        //
        // `scrollToElementTop` y no `scrollIntoView({block:"start"})` porque el
        // header es fijo y tapaba la primera línea del bloque.
        scrollToElementTop(this.$el);
      });
    },
    currentOaIndex() {
      this.$nextTick(() => this.scrollConfigTop());
    },
  },
  mounted() {
    this.fetchGrades().then(
      () => this.arrancarAutoplayInyectado(),
      () => {}
    );
  },
  beforeDestroy() {
    if (this.detenerAutoplay) this.detenerAutoplay();
    this.detenerAutoplay = null;
  },
  methods: {
    bump(obj, delta) {
      const next = (Number(obj.count) || 0) + delta;
      if (next < 0) return;
      if (delta > 0) {
        const oa = this.currentCountOa;
        let projected = next;
        if (oa && oa.indicators && oa.indicators.length) {
          projected = oa.indicators.reduce((sum, ind) => {
            if (ind === obj) return sum + next;
            return sum + (Number(ind.count) || 0);
          }, 0);
        }
        if (projected > 5) return;
      }
      this.$set(obj, "count", next);
      this.countsVersion += 1;
    },
    peldanoLabel(p) {
      return PELDANO_LABELS[p] || String(p);
    },
    peldanoSub(p) {
      return PELDANO_SUBS[p] || "";
    },
    peldanoCount(oa, p) {
      if (!oa || !oa.peldanos) return 0;
      return Number(oa.peldanos[p] || oa.peldanos[String(p)] || 0);
    },
    peldanoBloqueado(oa, p) {
      const blocked = (oa && oa.peldanosBloqueados) || {};
      return !!blocked[p] || !!blocked[String(p)];
    },
    peldanoMotivo(oa, p) {
      const blocked = (oa && oa.peldanosBloqueados) || {};
      return blocked[p] || blocked[String(p)] || "no disponible en este OA";
    },
    bumpPeldano(oa, p, delta) {
      if (!oa || this.peldanoBloqueado(oa, p)) return;
      const cur = this.peldanoCount(oa, p);
      const next = cur + delta;
      if (next < 0) return;
      if (delta > 0 && this.oaTotal(oa) - cur + next > PELDANOS_MAX_POR_OA) {
        return;
      }
      const map = Object.assign({}, oa.peldanos || {});
      map[p] = next;
      this.$set(oa, "peldanos", map);
      this.countsVersion += 1;
    },
    oaNumero(oa) {
      if (!oa) return null;
      if (oa.visualOaNumber != null) return String(oa.visualOaNumber);
      const m = String(oa.name || "").match(/(?:OA\s*)?(\d{1,2})\b/i);
      return m ? String(parseInt(m[1], 10)) : null;
    },
    ensurePeldanosDefaults(oa) {
      if (!oa) return;
      if (!oa.peldanos || typeof oa.peldanos !== "object") {
        this.$set(oa, "peldanos", Object.assign({}, DEFAULT_PELDANOS));
      }
      const gradeCode = Number(
        this.selectedGrade && this.selectedGrade.code
      );
      const num = this.oaNumero(oa);
      const porGrade =
        SIN_PELDANO_POR_GRADE[gradeCode] ||
        SIN_PELDANO_POR_GRADE[String(gradeCode)] ||
        {};
      const rawBlocked = Object.assign(
        {},
        (num && porGrade[num]) || {},
        (num && porGrade[String(num)]) || {},
        (num && porGrade[String(num).padStart(2, "0")]) || {}
      );
      // Motivos vienen con claves string "1"…"4".
      // A15 · peldaño 4 nunca se bloquea: el sin apoyo existe siempre.
      const blocked = {};
      Object.keys(rawBlocked).forEach((k) => {
        if (String(k) === "4" || Number(k) === 4) return;
        blocked[k] = rawBlocked[k];
        blocked[Number(k)] = rawBlocked[k];
      });
      this.$set(oa, "peldanosBloqueados", blocked);
      // Si un peldaño está bloqueado, bajar su contador a 0.
      if (Object.keys(blocked).length) {
        const map = Object.assign({}, oa.peldanos);
        Object.keys(blocked).forEach((p) => {
          map[p] = 0;
          map[Number(p)] = 0;
        });
        this.$set(oa, "peldanos", map);
      }
      // Contador legado en 0: la suma sale de peldanos.
      this.$set(oa, "count", 0);
    },
    oaTotal(oa) {
      if (!oa) return 0;
      if (this.usesPeldanosUi && oa.peldanos) {
        return [1, 2, 3, 4].reduce(
          (s, p) => s + this.peldanoCount(oa, p),
          0
        );
      }
      if (oa.indicators && oa.indicators.length) {
        return oa.indicators.reduce((s, i) => s + (Number(i.count) || 0), 0);
      }
      return Number(oa.count) || 0;
    },
    oaShortSummary(oa) {
      const text = (oa && (oa.description || oa.name)) || "";
      return text.length > 72 ? text.slice(0, 70) + "…" : text;
    },
    escapeHtml(value) {
      return String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    animateScroll(element, target, duration = 560) {
      if (!element) return;
      if (element._scrollAnimation) cancelAnimationFrame(element._scrollAnimation);
      const start = element.scrollTop;
      const change = target - start;
      const started = performance.now();
      const ease = (t) => 1 - Math.pow(1 - t, 3);
      const frame = (now) => {
        const progress = Math.min(1, (now - started) / duration);
        element.scrollTop = start + change * ease(progress);
        if (progress < 1) {
          element._scrollAnimation = requestAnimationFrame(frame);
        }
      };
      element._scrollAnimation = requestAnimationFrame(frame);
    },
    scrollConfigTop() {
      const scroller = this.$refs.configScroll;
      if (scroller) scroller.scrollTop = 0;
    },
    /** El origen de datos inyectado (el demo), o null en el producto real. */
    origenInyectado() {
      return this.dataSource || (this.context && this.context.demo) || null;
    },
    /**
     * Le entrega esta instancia al guión del demo, si es que hay uno.
     *
     * Es toda la concesión que este componente le hace al demo público: el
     * guión —cuando el demo va solo porque el visitante no toca nada— vive en
     * materialsDemoAutoplay.js y aprieta desde afuera los mismos métodos que
     * apretaría un dedo. Sin origen inyectado esto no hace absolutamente nada,
     * así que el camino del usuario que paga no cambia.
     */
    arrancarAutoplayInyectado() {
      const origen = this.origenInyectado();
      if (!origen || typeof origen.autoplay !== "function") return;
      if (this.detenerAutoplay) this.detenerAutoplay();
      this.detenerAutoplay = origen.autoplay(this) || null;
    },
    // Public: used by MaterialApplicationStep "Empezar de nuevo" (not shared resetChat).
    restartToPick() {
      this.generationToken += 1;
      this.step = "pick";
      this.bootError = "";
      this.selectedGrade = null;
      this.subjects = [];
      this.loadingSubjects = false;
      this.selectedSubject = null;
      this.oas = [];
      this.loadingOas = false;
      this.generating = false;
      this.createdName = "";
      this.pdfUrl = null;
      this.streamCards = [];
      this.streamProgress = 0;
      this.streamProgressLabel = "Preparando…";
      this.streamTitle = "Generando material";
      this.streamError = null;
      this.showStreamLoader = false;
      this.showDownloadCta = false;
      this.currentOaIndex = 0;
      this.objectivePulseStopped = false;
      this.countsVersion = 0;
      this.generationAvisos = [];
      this.codigoHoja = null;
      this.$nextTick(() => this.scrollConfigTop());
    },
    toggleOa(oa) {
      const scroller = this.$refs.configScroll;
      const previousTop = scroller ? scroller.scrollTop : 0;
      this.objectivePulseStopped = true;
      const next = !oa.selected;
      this.$set(oa, "selected", next);
      if (next && this.usesPeldanosUi) {
        this.ensurePeldanosDefaults(oa);
      }
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const nextScroll = this.$refs.configScroll;
          if (!nextScroll) return;
          nextScroll.scrollTop = previousTop;
          const max = Math.max(0, nextScroll.scrollHeight - nextScroll.clientHeight);
          const sample = nextScroll.querySelector(".pi-mat-oa");
          // One OA row at a time (gap 8px matches .pi-mat-oa-list).
          const step = sample
            ? Math.round(sample.getBoundingClientRect().height) + 8
            : 70;
          const target = Math.min(max, previousTop + step);
          this.animateScroll(nextScroll, target, 560);
        });
      });
    },
    goToIndicators() {
      this.currentOaIndex = 0;
      this.generating = false;
      if (this.usesPeldanosUi) {
        this.selectedOas.forEach((oa) => this.ensurePeldanosDefaults(oa));
      }
      this.step = "indicators";
      this.$nextTick(() => this.scrollConfigTop());
    },
    backFromCounts() {
      this.generating = false;
      if (this.currentOaIndex > 0) {
        this.currentOaIndex -= 1;
        return;
      }
      this.step = "oas";
    },
    advanceCounts() {
      if (!this.canAdvanceCounts || this.generating) return;
      if (this.currentOaIndex >= this.selectedOas.length - 1) {
        this.generate();
        return;
      }
      this.currentOaIndex += 1;
    },
    isMathSubject(name) {
      return /matem/i.test(String(name || ""));
    },
    isLanguageSubject(name) {
      const n = String(name || "").toLowerCase();
      return (
        n.includes("lenguaje") ||
        n.includes("lengua y literatura") ||
        n.includes("comunicación") ||
        n.includes("comunicacion")
      );
    },
    oaNeedsReadingText(oaOrItem) {
      const rt = Number(
        (oaOrItem && (oaOrItem.resourceType || oaOrItem.resource_type)) || 0
      );
      return rt >= 1 && rt <= 9;
    },
    isPrekGrade(grade) {
      if (!grade) return false;
      if (Number(grade.code) === 0) return true;
      const name = String(grade.original_name || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
      return (
        /pre[\s-]?k/.test(name) ||
        name === "pk" ||
        name.includes("parvular")
      );
    },
    isMotorGrade(grade) {
      return MOTOR_GRADES.includes(Number(grade && grade.code));
    },
    usesVisualStream() {
      const grade = this.selectedGrade;
      const subject = this.selectedSubject;
      if (!grade || !subject || !this.isMathSubject(subject.name)) return false;
      return this.isPrekGrade(grade) || this.isMotorGrade(grade);
    },
    onGradeSelect(event) {
      const raw = String(
        (event && event.target && event.target.value) || ""
      ).trim();
      const grade =
        raw === ""
          ? null
          : this.grades.find((g) => String(g.code) === raw) || null;
      this.selectedGrade = grade;
      this.selectedSubject = null;
      this.subjects = [];
      this.oas = [];
      this.bootError = "";
      if (!grade) return;
      this.loadingSubjects = true;
      this.api.getSubjects(grade.original_name).then(
        (data) => {
          this.subjects = (data || [])
            .filter((s) => !SPECIAL_SUBJECTS.includes(s.name))
            .map((s) => ({
              code: String(s.id),
              enum_code: s.enum_code,
              name: s.name,
            }));
          this.selectedSubject = null;
          this.loadingSubjects = false;
          this.$nextTick(() => {
            const el = this.$refs.subjectSelect;
            if (el) el.value = "";
          });
        },
        () => {
          this.subjects = [];
          this.selectedSubject = null;
          this.loadingSubjects = false;
          this.bootError = "No se pudieron cargar las asignaturas.";
        }
      );
    },
    onSubjectSelect(event) {
      const code = String(
        (event && event.target && event.target.value) || ""
      ).trim();
      this.selectedSubject =
        code === ""
          ? null
          : this.subjects.find((s) => String(s.code) === code) || null;
      this.oas = [];
      this.bootError = "";
    },
    fetchGrades() {
      this.loadingGrades = true;
      this.grades = [];
      this.bootError = "";
      return this.api.getGrades().then(
        (data) => {
          this.grades = ((data && data.grades) || [])
            .filter((g) => ![14, 15, 16].includes(Number(g[0])))
            .map((g) => ({
              code: Number(g[0]),
              original_name: g[1],
            }));
          this.loadingGrades = false;
        },
        (err) => {
          this.grades = [];
          this.loadingGrades = false;
          this.bootError = "No se pudieron cargar los cursos.";
          return Promise.reject(err);
        }
      );
    },
    goToOas() {
      if (!this.selectedSubject) return Promise.resolve();
      this.loadingOas = true;
      this.bootError = "";
      return this.api.getOas(this.selectedSubject.code).then(
        (data) => {
          const sorted = (data || []).slice().sort(
            (a, b) => (a.priority || 0) - (b.priority || 0)
          );
          let rows;
          const gradeCode = Number(this.selectedGrade && this.selectedGrade.code);
          const isNtPilotGrade = gradeCode === 0 || gradeCode === 1;
          if (isNtPilotGrade && this.isMathSubject(this.selectedSubject.name)) {
            // Solo OAs del piloto visual NT (01–12) en Prekínder y Kínder.
            rows = sorted.filter(
              (oa) =>
                isVisualPilot(oa.id, oa.name) ||
                isVisualPilot(oa.id, oa.description || "")
            );
            const usedPriorityFallback = rows.length < 6;
            if (usedPriorityFallback) {
              rows = sorted.slice(0, 12).map((oa, idx) => ({
                ...oa,
                visualOaNumber: idx + 1,
              }));
              console.warn(
                "[Materiales NT] OA names did not match PM NT pilots; " +
                  "using priority order as OA 01–12.",
                rows.map((r) => r.name)
              );
            }
          } else {
            // Básica (motor u otros): listar OAs de la asignatura elegida.
            rows = sorted;
          }
          this.oas = rows.map((oa) => ({
            id: oa.id,
            name: oa.name,
            description: oa.description,
            visualOaNumber: oa.visualOaNumber || null,
            resourceType: Number(oa.resource_type) || 0,
            resourceTypeDisplay: oa.resource_type_display || "",
            selected: false,
            count: 0,
            peldanos: Object.assign({}, DEFAULT_PELDANOS),
            peldanosBloqueados: {},
            indicators: (oa.subtags || [])
              .filter((i) => i.summary != null)
              .map((i, idx) => ({
                id: i.id,
                label: `Ind. ${idx + 1}: ${i.summary}`,
                description: i.name,
                count: 0,
              })),
          }));
          this.loadingOas = false;
          this.step = "oas";
        },
        () => {
          this.oas = [];
          this.loadingOas = false;
          this.bootError = "No se pudieron cargar los OAs.";
          this.step = "oas";
        }
      );
    },
    buildItems() {
      const items = [];
      // A12 · OA numérico y luego peldaño 1→4: la hoja tiene que mostrar la
      // progresión, no el orden de clic ni un p4 huérfano al tope.
      const oasOrdered = this.selectedOas.slice().sort((a, b) => {
        const na = Number(this.oaNumero(a)) || 0;
        const nb = Number(this.oaNumero(b)) || 0;
        return na - nb;
      });
      oasOrdered.forEach((oa) => {
        // Prek: nombre canónico PM NT. Básica motor: el nombre del tag (MA0x).
        const canonicalName =
          !this.isMotorGrade(this.selectedGrade) && oa.visualOaNumber != null
            ? `PM NT OA ${String(oa.visualOaNumber).padStart(2, "0")}`
            : oa.name;
        const base = {
          id: oa.id,
          name: canonicalName,
          description: oa.description,
        };
        const resourceType = Number(oa.resourceType) || 0;
        if (this.usesPeldanosUi && oa.peldanos) {
          [1, 2, 3, 4].forEach((p) => {
            const n = this.peldanoCount(oa, p);
            for (let k = 0; k < n; k += 1) {
              items.push({
                oa: base,
                subtag: null,
                resourceType,
                peldano: p,
              });
            }
          });
          return;
        }
        const inds = oa.indicators.filter((i) => i.count > 0);
        if (inds.length) {
          inds.forEach((i) => {
            for (let k = 0; k < i.count; k += 1) {
              items.push({
                oa: base,
                subtag: { id: i.id, description: i.description },
                resourceType,
              });
            }
          });
        } else if (oa.count > 0) {
          for (let k = 0; k < oa.count; k += 1) {
            items.push({ oa: base, subtag: null, resourceType });
          }
        }
      });
      return items;
    },
    questionImageUrl(q) {
      if (!q) return null;
      return q.image || q.image_url || (q.image_file && q.image_file.url) || null;
    },
    altTexts(q) {
      const alts = (q && (q.alternatives || q.special_alternatives)) || [];
      return alts
        .map((a) => (typeof a === "string" ? a : a.text || a.alternative || ""))
        .filter(Boolean)
        .slice(0, 4);
    },
    readingTypeLabel(rt) {
      const labels = {
        1: "Fábula",
        2: "Poema",
        3: "Novela",
        4: "Noticia",
        5: "Carta",
        6: "Biografía",
        7: "Expositivo",
        8: "Ensayo",
        9: "Historia",
      };
      return labels[Number(rt)] || "Texto";
    },
    makeReadingCard(rt, ti) {
      const label = this.readingTypeLabel(rt);
      return {
        num: `L${ti + 1}`,
        tag: label,
        family: { label: "Lectura", height: 220 },
        mode: "reading",
        statement: "Lee el siguiente texto:",
        typed: "Lee el siguiente texto:",
        typing: false,
        phase: "queued",
        mounted: false,
        drawCard: false,
        drawFrame: false,
        sceneHtml: "",
        sceneHeight: 220,
        fromMotorSvg: false,
        svgLayout: null,
        imageUrl: null,
        alternatives: [],
        altsVisible: false,
        question: null,
        sourceItem: null,
        resourceType: Number(rt) || 0,
        readingTitle: "",
        readingBody: "",
      };
    },
    makeStreamCard(item, idx, grade, subject) {
      const oaName = (item.oa && item.oa.name) || "";
      const oaId = (item.oa && item.oa.id) || null;
      void grade;
      void subject;
      const visual = this.usesVisualStream();
      const family = familyForOa(oaName, oaId);
      const peldano = item.peldano || null;
      const tagBase = oaName || "OA";
      return {
        num: idx + 1,
        tag: peldano ? `${tagBase} · ${peldano} ${this.peldanoLabel(peldano)}` : tagBase,
        family,
        mode: visual ? "visual" : "text",
        peldano,
        statement: "",
        typed: "",
        typing: false,
        phase: "queued",
        mounted: false,
        drawCard: false,
        drawFrame: false,
        sceneHtml: "",
        sceneHeight: family.height || 250,
        fromMotorSvg: false,
        svgLayout: null,
        imageUrl: null,
        alternatives: [],
        altsVisible: false,
        question: null,
        sourceItem: item,
      };
    },
    /**
     * Lenguaje: monta tarjeta(s) de lectura, streamea el texto dentro
     * (altura fija) y persiste SpecialText antes de las preguntas.
     */
    async streamReadingTexts({
      rts,
      school,
      grade,
      textsByRt,
      isAlive,
    }) {
      for (let ti = 0; ti < rts.length; ti += 1) {
        if (!isAlive()) return;
        const rt = rts[ti];
        const card = this.makeReadingCard(rt, ti);
        const isFirstVisible = this.streamCards.length === 0;

        this.streamProgress = Math.max(
          2,
          Math.round(((ti + 0.35) / (rts.length + 1)) * 14)
        );
        this.streamProgressLabel =
          rts.length > 1
            ? `Escribiendo texto ${ti + 1} de ${rts.length}…`
            : "Escribiendo texto de lectura…";

        this.streamCards.push(card);
        await this.$nextTick();
        if (isFirstVisible && this.$refs.streamCards) {
          this.$refs.streamCards.releaseBurst();
        }
        if (this.$refs.streamCards && this.$refs.streamCards.mountShell) {
          await this.$refs.streamCards.mountShell(card);
        } else {
          card.mounted = true;
          await wait(110);
        }
        if (isFirstVisible && this.$refs.streamCards) {
          await this.$refs.streamCards.scrollToCard(card.num);
        }
        if (isFirstVisible || this.showStreamLoader) {
          await wait(430);
          if (
            this.$refs.streamCards &&
            this.$refs.streamCards.whenLoaderDone
          ) {
            await this.$refs.streamCards.whenLoaderDone();
          }
          this.showStreamLoader = false;
        }

        card.phase = "active";
        card.drawCard = true;
        card.typing = true;
        await wait(180);

        try {
          // eslint-disable-next-line no-await-in-loop
          const saved = await this.api.generateReadingText(
            {
              school,
              gradeCode: grade.code,
              resourceTypeId: rt,
              theme: "amistad y naturaleza",
              details:
                "Texto breve y claro para comprensión lectora en clase, " +
                `adecuado a ${grade.original_name}.`,
              onProgress: ({ title, content }) => {
                if (!isAlive()) return;
                if (title) card.readingTitle = title;
                card.readingBody = content || "";
                if (
                  this.$refs.streamCards &&
                  this.$refs.streamCards.scrollReadingBody
                ) {
                  this.$refs.streamCards.scrollReadingBody(card.num);
                }
              },
            }
          );
          textsByRt[rt] = saved;
          if (saved) {
            card.readingTitle = saved.title || card.readingTitle;
            card.readingBody = saved.content || card.readingBody;
          }
        } catch (textErr) {
          console.warn("reading text generation failed", textErr);
          card.phase = "error";
          card.typing = false;
          throw new Error(
            "No se pudo crear el texto de lectura para el material."
          );
        }

        if (!isAlive()) return;
        card.typing = false;
        card.phase = "complete";
        card.drawCard = false;
        await wait(520);
      }
    },
    applyVisualScene(card, q) {
      const vm = (q && q.visual_math) || null;
      const imageUrl = this.questionImageUrl(q);
      card.imageUrl = imageUrl;
      card.statement =
        (q && (q.question || q.enunciado || (q.ejercicio && q.ejercicio.enunciado))) ||
        "Observa y responde.";
      if (vm && vm.tipo) {
        const oaNum =
          (card.sourceItem &&
            card.sourceItem.oa &&
            resolvePrekVisualOaNumber(card.sourceItem.oa.id, card.sourceItem.oa.name)) ||
          null;
        card.family = familyForTipo(vm.tipo, oaNum);
      }
      // SVG del motor (7 cáscaras): misma geometría que el PDF. Si no viene,
      // caemos a los builders JS de siempre —hoy el generador legacy no emite
      // cáscara+estímulo, así que este if es el camino del futuro cercano.
      if (q && q.svg) {
        card.fromMotorSvg = true;
        card.sceneHtml = q.svg;
        card.sceneHeight = q.svg_alto || 250;
        card.svgLayout = q.svg_layout || null;
        card.family = { ...card.family, height: card.sceneHeight };
        return;
      }
      card.fromMotorSvg = false;
      card.svgLayout = null;
      const scene = buildSceneFromConfig(vm, { imageUrl });
      card.sceneHtml = scene.html;
      card.sceneHeight = scene.height;
      card.family = { ...card.family, height: scene.height };
      if (!scene.usedOfficial) {
        console.warn(
          "[prekStreamScenes] card",
          card.num,
          "using PNG fallback"
        );
      }
    },
    async fetchCardQuestion(card, basePayload, diversity = {}) {
      try {
        if (card.mode === "visual") {
          const item = Object.assign({}, card.sourceItem);
          if (card.peldano) item.peldano = card.peldano;
          if (card.variacionI != null) item.variacion_i = card.variacionI;
          if (card.planContexto) item.plan_contexto = card.planContexto;
          const res = await this.api.generateVisualMathQuestions({
            ...basePayload,
            items: [item],
            used_tipos: diversity.usedTipos || [],
            used_enunciados: diversity.usedEnunciados || [],
          });
          if (res && Array.isArray(res.avisos) && res.avisos.length) {
            this.generationAvisos = this.generationAvisos.concat(res.avisos);
            console.warn("[MaterialAiGenerate] avisos (ocultos al usuario)", res.avisos);
          }
          const q = (res && res.questions && res.questions[0]) || null;
          if (q) {
            card.question = q;
            if (q.peldano) card.peldano = q.peldano;
            this.applyVisualScene(card, q);
            return q;
          }
          if (res && res.detail) {
            this.generationAvisos.push({
              nivel: "",
              oa: card.tag || "OA",
              peldano: card.peldano || 0,
              motivo: res.detail,
              accion: "tarjeta omitida",
            });
            console.warn(
              "[MaterialAiGenerate] tarjeta omitida",
              card.tag,
              "peldano",
              card.peldano,
              res.detail
            );
            card.phase = "error";
            card.statement = "";
          }
        } else {
          const rt = Number(
            card.sourceItem && card.sourceItem.resourceType
          ) || 0;
          const savedText =
            (diversity.textsByRt && diversity.textsByRt[rt]) || null;
          const payload = {
            ...basePayload,
            items: [card.sourceItem],
          };
          // Lenguaje + texto: el worker usa text_id como TEXTO DE REFERENCIA.
          // Sin texto, dejamos subject (otras asignaturas / OA sin resource_type).
          if (savedText && savedText.id) {
            payload.text_id = savedText.id;
            delete payload.subject;
          }
          const textRes = await this.api.generatePageQuestions(
            payload
          );
          const q =
            (textRes && textRes.questions && textRes.questions[0]) || null;
          if (q) {
            card.question = q;
            card.statement = q.question || "Lee y responde.";
            card.alternatives = this.altTexts(q);
            while (card.alternatives.length < 4) {
              card.alternatives.push("…");
            }
            return q;
          }
        }
      } catch (err) {
        console.warn("card generation failed", err);
        const detail =
          (err && err.response && err.response.data &&
            (err.response.data.detail || err.response.data.error)) ||
          (err && err.message) ||
          "error de red";
        this.generationAvisos.push({
          nivel: "",
          oa: card.tag || "OA",
          peldano: card.peldano || 0,
          motivo: String(detail),
          accion: "tarjeta omitida; el resto de la hoja sigue",
        });
        console.warn(
          "[MaterialAiGenerate] tarjeta omitida (catch)",
          card.tag,
          "peldano",
          card.peldano,
          detail
        );
        card.statement = "";
        card.phase = "error";
      }
      return null;
    },
    buildPagesForSave(orderedEntries, textsByRt) {
      const subject = this.selectedSubject;
      const isLang = this.isLanguageSubject(subject && subject.name);
      const hasTexts =
        isLang && textsByRt && Object.keys(textsByRt).length > 0;
      if (!hasTexts) {
        return [
          {
            index: 0,
            questions: this.questionsForSave(
              orderedEntries.map((e) => e.question)
            ),
            text: null,
            images: [],
            audio: null,
            additional_instruction: "",
            eje: null,
          },
        ];
      }
      const groups = new Map();
      orderedEntries.forEach((e) => {
        const rt = Number(e.resourceType) || 0;
        if (!groups.has(rt)) groups.set(rt, []);
        groups.get(rt).push(e.question);
      });
      const pages = [];
      let index = 0;
      groups.forEach((questions, rt) => {
        const text = textsByRt[rt] || null;
        pages.push({
          index,
          questions: this.questionsForSave(questions),
          text: text || null,
          show_text: rt !== 9,
          images: [],
          audio: null,
          additional_instruction: "",
          eje: null,
        });
        index += 1;
      });
      return pages;
    },
    diversityKeyFromQuestion(q) {
      const vm = (q && q.visual_math) || {};
      const bits = [
        vm.tipo || "",
        vm.objeto || vm.asset || "",
        vm.cantidad != null ? `n=${vm.cantidad}` : "",
        vm.total != null ? `t=${vm.total}` : "",
        vm.group1 != null ? `g=${vm.group1}+${vm.group2}` : "",
        vm.unidad_count != null ? `u=${vm.unidad_count}` : "",
        vm.figura || "",
        vm.dentro || "",
      ].filter(Boolean);
      return bits.join(":");
    },
    trackDiversity(q, statement, usedTipos, usedEnunciados) {
      const vm = (q && q.visual_math) || {};
      // Plain tipo first so backend _pick_* can force alternate layouts.
      if (vm.tipo) usedTipos.push(String(vm.tipo));
      const dKey = this.diversityKeyFromQuestion(q);
      if (dKey && dKey !== vm.tipo) usedTipos.push(dKey);
      // El hermano viaja con su DIBUJO, no sólo con su texto. El backend
      // (`_hermano`) acepta las dos formas, así que esto es compatible hacia
      // atrás; pero mandando sólo el enunciado, el guarda de dibujo repetido no
      // podía disparar nunca en producción y el modelo tampoco veía qué había
      // dibujado su hermano. Medido sobre una corrida de 5º y 6º: 4 de 110
      // pares de peldaños del mismo OA salieron con el estímulo idéntico.
      if (statement) {
        usedEnunciados.push(
          vm.estimulo
            ? { enunciado: statement, estimulo: vm.estimulo, oa: vm.oa || "" }
            : statement
        );
      }
    },
    questionsForSave(questions) {
      // El PDF solo necesita el id de SpecialQuestion. El SVG del stream hincha
      // el POST y no se persiste; lo quitamos antes de createChatbotMaterial.
      return (questions || []).map((q) => {
        if (!q || typeof q !== "object") return q;
        const {
          svg,
          svg_layout,
          svg_ancho,
          svg_alto,
          svg_formato,
          svg_motivo,
          visual_math,
          ...rest
        } = q;
        void svg;
        void svg_layout;
        void svg_ancho;
        void svg_alto;
        void svg_formato;
        void svg_motivo;
        void visual_math;
        return rest;
      });
    },
    async generate() {
      const items = this.buildItems();
      if (!items.length) return;
      this.generating = true;
      this.streamError = null;
      this.generationToken += 1;
      const token = this.generationToken;
      const isAlive = () => token === this.generationToken;
      const grade = this.selectedGrade;
      const subject = this.selectedSubject;
      const school = this.$store.state.login.school;
      const tClick = performance.now();
      let tFirstCardAnimate = null;

      try {
        if (!grade || !subject) {
          throw new Error("Falta curso o asignatura");
        }

        // Progressive reveal: no N "Preparando…" placeholders.
        this.streamCards = [];
        this.generationAvisos = [];
        this.showStreamLoader = true;
        this.showDownloadCta = false;
        this.pdfUrl = null;
        this.codigoHoja = null;
        this.streamProgress = 2;
        this.streamProgressLabel = "Preparando escena…";
        this.streamTitle = "Creando material con IA";
        this.step = "streaming";
        await this.$nextTick();
        // Do not scroll the page/chat — only the stream feed scrolls.

        const basePayload = {
          school,
          type: 0,
          age: grade.code,
          difficulty: "media",
          subject: subject.name,
        };

        const textsByRt = {};
        // Ordinal dentro del OA (0…N-1): alimenta recetas/NT. No reiniciar
        // por peldaño — con varios del mismo peldaño se repetía la pregunta.
        const oaOrdinal = {};
        const plan = items.map((item, idx) => {
          const card = this.makeStreamCard(item, idx, grade, subject);
          const oaKey =
            (item.oa && (item.oa.id || item.oa.name)) || `i-${idx}`;
          const i = oaOrdinal[oaKey] || 0;
          oaOrdinal[oaKey] = i + 1;
          card.variacionI = i;
          return card;
        });

        const usedTipos = [];
        const usedEnunciados = [];

        // Splash primero; luego (Lenguaje) tarjeta de lectura en stream.
        const messagesDone =
          (this.$refs.streamCards &&
            this.$refs.streamCards.whenMessagesDone &&
            this.$refs.streamCards.whenMessagesDone()) ||
          wait(1900);

        await messagesDone;
        if (!isAlive()) return;

        // Expandir pedido (techo abanico + peldaños ausentes). El GPT del
        // planificador puede estar apagado; el endpoint igual devuelve filas.
        if (this.usesPeldanosUi && plan.length > 0) {
          this.streamProgressLabel = "Ajustando la hoja al objetivo…";
          try {
            const planRes =
              await this.api.planificarMaterialProgresion({
                school,
                age: grade.code,
                items: plan.map((c) => c.sourceItem),
              });
            if (planRes && Array.isArray(planRes.avisos) && planRes.avisos.length) {
              const pedagogicos = planRes.avisos.filter(
                (a) =>
                  a &&
                  a.oa !== "*" &&
                  String(a.motivo || "").indexOf("planificador apagado") < 0
              );
              this.generationAvisos = this.generationAvisos.concat(pedagogicos);
              if (pedagogicos.length) {
                console.info(
                  "[MaterialAiGenerate] avisos pedagógicos",
                  pedagogicos
                );
              }
            }
            const contextos = (planRes && planRes.contextos) || {};
            const planRows = (planRes && planRes.plan) || [];
            if (planRows.length) {
              // Reconstruir tarjetas desde el plan (puede venir recortado).
              const byOa = {};
              plan.forEach((c) => {
                const key =
                  (c.sourceItem &&
                    c.sourceItem.oa &&
                    (c.sourceItem.oa.id || c.sourceItem.oa.name)) ||
                  c.tag;
                if (!byOa[key]) byOa[key] = [];
                byOa[key].push(c);
              });
              const used = new Set();
              const rebuilt = [];
              const oaOrdinal = {};
              planRows.forEach((row, idx) => {
                const n = Number(row.n) || idx + 1;
                const oaKey = row.oa || "";
                const poolKey = Object.keys(byOa).find(
                  (k) =>
                    byOa[k].length &&
                    (String(k).indexOf(oaKey) >= 0 ||
                      String(
                        (byOa[k][0].sourceItem &&
                          byOa[k][0].sourceItem.oa &&
                          byOa[k][0].sourceItem.oa.name) ||
                          ""
                      ).indexOf(oaKey) >= 0)
                );
                let card = null;
                if (poolKey) {
                  const pool = byOa[poolKey];
                  const wantP = Number(row.peldano) || 0;
                  const ix = pool.findIndex(
                    (c, i) =>
                      !used.has(`${poolKey}:${i}`) &&
                      Number(c.peldano) === wantP
                  );
                  const pick =
                    ix >= 0
                      ? ix
                      : pool.findIndex((c, i) => !used.has(`${poolKey}:${i}`));
                  if (pick >= 0) {
                    used.add(`${poolKey}:${pick}`);
                    card = pool[pick];
                  }
                }
                if (!card) {
                  card = this.makeStreamCard(
                    plan[0] && plan[0].sourceItem
                      ? Object.assign({}, plan[0].sourceItem, {
                          peldano: row.peldano,
                        })
                      : { oa: { name: row.oa }, peldano: row.peldano },
                    idx,
                    grade,
                    subject
                  );
                }
                const pPlan = Number(row.peldano) || card.peldano;
                if (pPlan >= 1 && pPlan <= 4) {
                  card.peldano = pPlan;
                  if (card.sourceItem) {
                    card.sourceItem = Object.assign({}, card.sourceItem, {
                      peldano: pPlan,
                    });
                  }
                  const oaName =
                    (card.sourceItem &&
                      card.sourceItem.oa &&
                      card.sourceItem.oa.name) ||
                    row.oa ||
                    "OA";
                  card.tag = `${oaName} · ${pPlan} ${this.peldanoLabel(pPlan)}`;
                }
                const ordKey =
                  (card.sourceItem &&
                    card.sourceItem.oa &&
                    (card.sourceItem.oa.id || card.sourceItem.oa.name)) ||
                  row.oa ||
                  `i-${idx}`;
                const iOrd = oaOrdinal[ordKey] || 0;
                oaOrdinal[ordKey] = iOrd + 1;
                card.variacionI = iOrd;
                card.num = n;
                let ctx =
                  contextos[n] || contextos[String(n)] || "";
                const extras = [];
                if (row.asset) {
                  extras.push(
                    `Asset OBLIGATORIO de esta fila (no otro): ${row.asset}`
                  );
                }
                if (row.contexto) {
                  extras.push(`Contexto del enunciado: ${row.contexto}`);
                }
                if (row.rango) extras.push(`Rango: ${row.rango}`);
                if (extras.length) {
                  ctx = (ctx ? ctx + "\n\n" : "") + extras.join("\n");
                }
                if (ctx) {
                  card.planContexto = ctx;
                  if (card.sourceItem) {
                    card.sourceItem = Object.assign({}, card.sourceItem, {
                      plan_contexto: ctx,
                    });
                  }
                }
                rebuilt.push(card);
              });
              plan.splice(0, plan.length, ...rebuilt);
            }
          } catch (err) {
            console.warn("expandir/planificar progresión falló; sigo sin plan", err);
          }
          if (!isAlive()) return;
        }

        if (this.isLanguageSubject(subject.name)) {
          const rts = [
            ...new Set(
              items
                .map((it) => Number(it.resourceType) || 0)
                .filter((rt) => rt >= 1 && rt <= 9)
            ),
          ];
          if (rts.length) {
            await this.streamReadingTexts({
              rts,
              school,
              grade,
              textsByRt,
              isAlive,
            });
            if (!isAlive()) return;
          }
        }

        // Preguntas: prefetch de la 1ª con el texto ya guardado (text_id).
        let nextFetch = plan.length
          ? this.fetchCardQuestion(plan[0], basePayload, {
              usedTipos,
              usedEnunciados,
              textsByRt,
            })
          : null;

        const orderedQuestions = [];
        for (let i = 0; i < plan.length; i += 1) {
          if (!isAlive()) return;
          const card = plan[i];
          this.streamProgress = Math.max(
            2,
            Math.round((i / plan.length) * 92)
          );
          this.streamProgressLabel = `Generando pregunta ${i + 1} de ${
            plan.length
          }…`;

          const tGpt0 = performance.now();
          if (!card.question) {
            await nextFetch;
          }
          const tGptMs = Math.round(performance.now() - tGpt0);
          console.log(
            `[stream-timing] T_per_question_gpt card=${card.num} ms=${tGptMs}`
          );

          if (card.question) {
            // Antes del prefetch para que la siguiente carta vea diversidad.
            this.trackDiversity(
              card.question,
              card.statement,
              usedTipos,
              usedEnunciados
            );
          }

          // Prefetch SIEMPRE antes de un possible `continue`: si esta carta
          // falló (400) y no arrancamos la siguiente, el resto del lote se
          // salta sin pedir nada — en 3º quedó 1 de 26 por eso.
          if (i + 1 < plan.length && !plan[i + 1].question) {
            nextFetch = this.fetchCardQuestion(plan[i + 1], basePayload, {
              usedTipos,
              usedEnunciados,
              textsByRt,
            });
          }

          if (!card.question && card.mode === "visual") {
            console.warn("skipping card without question", card.num);
            continue;
          }
          if (!card.question) continue;

          const isFirstVisible = this.streamCards.length === 0;

          // HTML v4: mount card, then burst-fade splash over it (no blank gap).
          this.streamCards.push(card);
          await this.$nextTick();
          if (isFirstVisible && this.$refs.streamCards) {
            this.$refs.streamCards.releaseBurst();
          }
          if (this.$refs.streamCards && this.$refs.streamCards.mountShell) {
            await this.$refs.streamCards.mountShell(card);
          } else {
            card.mounted = true;
            await wait(110);
          }
          // Pin first card at top of the feed (inner scroll only).
          if (isFirstVisible && this.$refs.streamCards) {
            await this.$refs.streamCards.scrollToCard(card.num);
          }

          if (isFirstVisible || this.showStreamLoader) {
            await wait(430);
            if (
              this.$refs.streamCards &&
              this.$refs.streamCards.whenLoaderDone
            ) {
              await this.$refs.streamCards.whenLoaderDone();
            }
            this.showStreamLoader = false;
          }

          if (tFirstCardAnimate == null) {
            tFirstCardAnimate = performance.now();
            console.log(
              "[stream-timing] T_first_card_animate_start_ms=",
              Math.round(tFirstCardAnimate - tClick)
            );
            if (tFirstCardAnimate - tClick > 15000) {
              console.error(
                "[stream-timing] FAIL: T_first_card > 15s (proxy/timeout/fallback)"
              );
            }
          }

          const tAnim0 = performance.now();
          if (this.$refs.streamCards && this.$refs.streamCards.animateCard) {
            await this.$refs.streamCards.animateCard(card, token, isAlive);
          }
          if (!isAlive()) return;
          console.log(
            `[stream-timing] T_per_question_gpt_plus_animate card=${
              card.num
            } ms=${Math.round(performance.now() - tAnim0 + tGptMs)}`
          );

          orderedQuestions.push({
            question: card.question,
            resourceType: Number(
              (card.sourceItem && card.sourceItem.resourceType) || 0
            ),
          });
          this.streamProgress = Math.round(((i + 1) / plan.length) * 92);
          this.streamProgressLabel = `Pregunta ${i + 1} de ${plan.length} lista`;
        }

        if (!isAlive()) return;
        if (!orderedQuestions.length) {
          throw new Error("No se generaron preguntas");
        }

        if (orderedQuestions.length < plan.length) {
          this.generationAvisos.push({
            oa: "*",
            codigo: "hoja_corta",
            motivo:
              "Pediste " +
              plan.length +
              " ejercicios y salieron " +
              orderedQuestions.length,
            accion:
              "nos disculpamos por ese inconveniente; estamos tratando de mejorar. Si tienes cualquier feedback, por favor compártelo abajo",
          });
        }

        this.showDownloadCta = true;
        this.streamProgress = 96;
        this.streamProgressLabel = "Armando PDF…";
        this.streamTitle = "¡Listo! Preparando descarga";
        this.showStreamLoader = false;
        const createdMaterial = {
          name: `${grade.original_name} ${subject.name}`,
          pages: this.buildPagesForSave(orderedQuestions, textsByRt),
          duration: 60,
          style: 0,
          description: "Material generado con IA",
          profession: 6,
          topic: null,
          subject: subject.enum_code,
          grade: grade.code,
          school,
          created_by: this.$store.state.login.user_id,
          ia_generated: true,
        };
        const response = await this.api.createMaterial(
          createdMaterial,
          // Visual Prek–8º math: preguntas en main. Texto (otra asignatura /
          // media / Lenguaje): preguntas en worker → PDF también vía ticket.
          { persistLocal: this.usesVisualStream() }
        );
        if (!isAlive()) return;
        this.createdName = response.name;
        this.pdfUrl = response.pdf || null;
        this.codigoHoja = response.codigo_hoja || null;
        this.streamProgress = 100;
        this.streamProgressLabel = this.pdfUrl
          ? "Material listo — descarga abajo"
          : "Material listo";
        this.streamTitle = response.name
          ? `Listo: ${response.name}`
          : "Material listo";
        this.step = "done";
        this.showStreamLoader = false;
        this.$emit("material-done");
      } catch (error) {
        console.log(error);
        const data = error && error.response && error.response.data;
        let detail = "";
        if (typeof data === "string") detail = data;
        else if (data && data.detail) detail = data.detail;
        else if (data && data.error) detail = data.error;
        else if (error && error.message) detail = error.message;
        const msg = detail
          ? `No se pudo guardar el material: ${detail}`
          : "No se pudo generar el material.";
        this.streamError = msg;
        this.$toasted.error(msg);
        this.showStreamLoader = false;
        this.showDownloadCta = false;
        this.streamProgress = Math.min(this.streamProgress, 92);
        this.step = "indicators";
      } finally {
        this.generating = false;
      }
    },
  },
};

</script>

<style scoped>
.aig {
  --pi-main: var(--main-color, #e84855);
  --pi-main-dark: var(--secondary-color, #bf2f3d);
  --pi-deep: var(--product-deep, #52141c);
  --pi-rgb: var(--product-rgb, 232, 72, 85);
  --pi-muted: #6d7788;
  font-family: "Ubuntu", Arial, sans-serif;
  color: #1a2332;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.pi-mat {
  border: 1px solid rgba(var(--pi-rgb), 0.2);
  border-radius: 22px;
  background: #fff;
  box-shadow: 0 10px 24px rgba(26, 35, 50, 0.06);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  /* Guarantees room for ~8 OA / indicator rows + head/footer. */
  min-height: 620px;
  height: auto;
  max-height: none;
}
.pi-mat-head {
  padding: 12px 14px;
  border-bottom: 1px dashed rgba(var(--pi-rgb), 0.22);
  background: linear-gradient(90deg, #fff6f7, #fff 80%);
  flex-shrink: 0;
}
.pi-mat-head strong {
  display: block;
  color: var(--pi-deep);
  font-size: 1rem;
  font-weight: 950;
}
.pi-mat-head > span {
  display: block;
  margin-top: 3px;
  color: var(--pi-muted);
  font-size: 0.8rem;
  font-weight: 760;
  line-height: 1.3;
}
.pi-mat-hint {
  margin: 8px 0 0;
  color: var(--pi-muted);
  font-size: 0.82rem;
  font-weight: 650;
  line-height: 1.35;
}
.course-context {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 7px;
  color: var(--pi-main);
  font-size: 0.65rem;
  font-weight: 850;
  letter-spacing: 0.055em;
  text-transform: uppercase;
}
.course-context i {
  color: color-mix(in srgb, var(--pi-main) 35%, #fff);
  font-style: normal;
}
.pi-mat-stage {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.pi-mat-scroll {
  flex: 1 1 auto;
  /* ~8 filas de OA/indicador (~52px) visibles sin apretar. */
  min-height: 440px;
  overflow-y: auto;
  padding: 10px 14px;
  -webkit-overflow-scrolling: touch;
}
.pi-mat-max-hint {
  margin: 0 0 10px;
  color: color-mix(in srgb, var(--pi-main) 45%, #6d7788);
  font-size: 0.74rem;
  font-weight: 750;
}
.pi-mat-footer {
  flex-shrink: 0;
  padding: 10px 14px 12px;
  border-top: 1px dashed rgba(var(--pi-rgb), 0.24);
  background: #fff;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  position: sticky;
  bottom: 0;
  z-index: 3;
}
.pi-mat-footer--split {
  justify-content: space-between;
}
.pi-mat-footer--split .pi-direct-btn {
  flex: 1;
  max-width: 220px;
}
.pi-mat-loading,
.pi-mat-empty {
  padding: 12px 0;
  color: var(--pi-muted);
  font-weight: 760;
  font-size: 0.84rem;
}
.pi-mat-oa-list {
  display: grid;
  gap: 8px;
}
.pi-mat-pick {
  display: grid;
  gap: 14px;
  padding: 6px 0 10px;
}
.pi-mat-field {
  display: grid;
  gap: 6px;
  margin: 0;
}
.pi-mat-field > span {
  color: var(--pi-main);
  font-size: 0.72rem;
  font-weight: 850;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.pi-mat-select {
  width: 100%;
  border: 2px dashed rgba(var(--pi-rgb), 0.28);
  background: #fff;
  border-radius: 12px;
  padding: 10px 12px;
  color: #1a2332;
  font-size: 0.92rem;
  font-weight: 700;
}
.pi-mat-select:disabled {
  opacity: 0.55;
}
.pi-mat-oa {
  width: 100%;
  border: 2px dashed rgba(var(--pi-rgb), 0.24);
  background: #fff;
  border-radius: 14px;
  padding: 9px 11px;
  text-align: left;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 9px;
  align-items: start;
  cursor: pointer;
}
.pi-mat-oa.is-selected {
  background: var(--product-soft, #fff8f9);
  border-color: rgba(var(--pi-rgb), 0.5);
}
.pi-mat-oa.is-pulse-guide {
  animation: piMaterialSelectGlow 1.25s ease-in-out infinite;
}
.pi-mat-oa-check {
  width: 22px;
  height: 22px;
  border-radius: 8px;
  border: 2px dashed rgba(var(--pi-rgb), 0.35);
  display: grid;
  place-items: center;
  color: #fff;
  background: #fff;
  font-weight: 950;
  font-size: 0.76rem;
}
.pi-mat-oa.is-selected .pi-mat-oa-check {
  background: var(--pi-main);
  border-color: var(--pi-main);
}
.pi-mat-oa-copy {
  min-width: 0;
}
.pi-mat-oa-copy strong {
  display: block;
  color: var(--pi-deep);
  font-size: 0.86rem;
  font-weight: 950;
}
.pi-mat-oa-copy span {
  display: block;
  margin-top: 2px;
  color: var(--pi-muted);
  font-size: 0.74rem;
  font-weight: 760;
  line-height: 1.25;
}
.pi-mat-counts-lead {
  margin: 0 0 10px;
  color: var(--pi-deep);
  font-weight: 900;
  font-size: 0.84rem;
}
.pi-mat-count-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  padding: 8px 0;
  border-top: 1px solid rgba(var(--pi-rgb), 0.1);
}
.pi-mat-count-row.is-disabled-peldano {
  opacity: 0.45;
}
.pi-mat-count-row.is-disabled-peldano .pi-count-stepper button {
  cursor: not-allowed;
}
.pi-mat-peldano-label {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  max-width: 16rem;
}
.pi-mat-peldano-sub {
  display: block;
  color: #8a93a3;
  font-weight: 550;
  font-size: 0.68rem;
  line-height: 1.25;
}
.pi-mat-peldano-motivo {
  display: block;
  color: color-mix(in srgb, var(--pi-main) 40%, #6d7788);
  font-weight: 650;
  font-size: 0.68rem;
}
.pi-mat-count-row span {
  color: #344052;
  font-size: 0.78rem;
  font-weight: 780;
  line-height: 1.28;
}
.pi-count-stepper {
  display: flex;
  align-items: center;
  gap: 6px;
}
.pi-count-stepper button {
  width: 30px;
  height: 30px;
  border-radius: 11px;
  border: 2px dashed rgba(var(--pi-rgb), 0.34);
  background: #fff;
  color: var(--pi-main-dark);
  font-weight: 950;
  cursor: pointer;
}
.pi-count-stepper b {
  min-width: 30px;
  height: 30px;
  border-radius: 11px;
  background: var(--product-soft, #fff1f3);
  border: 1px dashed rgba(var(--pi-rgb), 0.28);
  display: grid;
  place-items: center;
  color: var(--pi-main-dark);
  font-size: 0.85rem;
}
.pi-direct-btn {
  width: 100%;
  min-height: 44px;
  border: 0;
  border-radius: 14px;
  padding: 0 16px;
  background: var(--pi-main);
  color: #fff;
  font-weight: 800;
  cursor: pointer;
}
.pi-direct-btn.secondary {
  background: #fff;
  color: var(--pi-main-dark);
  border: 2px dashed rgba(var(--pi-rgb), 0.42);
}
.pi-direct-btn.pi-pulse-cta {
  animation: piMaterialButtonPulse 1.05s ease-in-out infinite;
}
.pi-direct-btn:disabled {
  opacity: 0.45;
  background: #fff;
  color: var(--pi-muted);
  border: 2px dashed rgba(var(--pi-rgb), 0.26);
  cursor: not-allowed;
  animation: none;
}
.pi-direct-btn.is-loading,
.pi-direct-btn.is-loading:disabled {
  opacity: 1;
  background: var(--pi-main);
  color: #fff;
  border: 0;
  cursor: wait;
  animation: none;
}
@keyframes piMaterialSelectGlow {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(var(--pi-rgb), 0.12);
  }
  50% {
    box-shadow: 0 0 0 5px rgba(var(--pi-rgb), 0.1);
  }
}
@keyframes piMaterialButtonPulse {
  0%,
  100% {
    transform: translateY(0);
    box-shadow: 0 0 0 0 rgba(var(--pi-rgb), 0.35);
  }
  50% {
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(var(--pi-rgb), 0.28);
  }
}
.aig-error {
  margin-top: 12px;
  font-size: 0.88rem;
  color: #b91c1c;
}
.aig-avisos {
  margin: 10px 0 0;
  padding: 0 0 0 1.1em;
  color: #5b6472;
  font-size: 0.78rem;
  font-weight: 650;
  line-height: 1.35;
}
.aig-avisos-pedagogicos {
  margin: 10px 0 0;
  padding: 8px 12px;
  list-style: none;
  background: #f8fafc;
  border-left: 3px solid #64748b;
  color: #334155;
  font-size: 0.82rem;
  line-height: 1.4;
}
.aig-avisos-pedagogicos li + li {
  margin-top: 4px;
}
.aig-done {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}
.aig-done-badge {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #10b981;
  color: #fff;
  font-weight: 900;
  display: flex;
  align-items: center;
  justify-content: center;
}
.aig-done-text {
  margin: 0;
  font-size: 0.95rem;
  color: #374151;
}
.aig-stream {
  flex: 1 1 auto;
  min-height: 0;
  /* Floor for desktop; on phone cap to viewport so the feed can scroll. */
  height: min(720px, calc(100dvh - 120px));
  max-height: calc(100dvh - 100px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(var(--pi-rgb), 0.2);
  border-radius: 22px;
  background: #fff;
  box-shadow: 0 10px 24px rgba(26, 35, 50, 0.06);
}
.aig-stream ::v-deep .mss {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 12px 14px 0;
}
.aig-stream ::v-deep .mss-head {
  flex-shrink: 0;
}
.aig-stream ::v-deep .viewport {
  flex: 1 1 auto;
  min-height: 0;
  /* Do NOT set height:auto — that kills the inner feed scroll on mobile. */
}
.aig-stream-footer {
  flex-shrink: 0;
  display: grid;
  gap: 10px;
  margin-top: 0;
  padding: 10px 14px 12px;
  border-top: 1px dashed rgba(var(--pi-rgb), 0.24);
  background: #fff;
  position: sticky;
  bottom: 0;
  z-index: 3;
}
.aig-stream-footer .pi-direct-btn,
.aig-stream-footer a.pi-direct-btn,
.aig-stream-footer a.pi-direct-btn:hover,
.aig-stream-footer a.pi-direct-btn:visited,
.aig-stream-footer a.pi-direct-btn:active {
  width: 100%;
  text-decoration: none !important;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--pi-main);
  color: #fff !important;
}
.aig-stream-footer .pi-direct-btn.is-waiting,
.aig-stream-footer .pi-direct-btn.is-waiting:disabled {
  opacity: 0.45;
  background: #fff;
  color: var(--pi-muted) !important;
  border: 2px dashed rgba(var(--pi-rgb), 0.26);
  cursor: not-allowed;
  animation: none;
}
.aig-stream-footer .pi-direct-btn.is-loading,
.aig-stream-footer .pi-direct-btn.is-loading:disabled {
  opacity: 1;
  background: var(--pi-main);
  color: #fff !important;
  border: 0;
  cursor: wait;
  animation: piMaterialButtonPulse 1.05s ease-in-out infinite;
}
@media (max-width: 575.98px) {
  .pi-mat-footer--split {
    flex-direction: column;
  }
  .pi-mat-footer--split .pi-direct-btn {
    max-width: none;
  }
  .aig-stream {
    height: calc(100dvh - 110px);
    max-height: calc(100dvh - 110px);
    min-height: 420px;
    border-radius: 16px;
  }
}
</style>
